import { IIBCoralHandler } from '@services/coralservices/IIBCoralHandler';
import { AFTXCoralHandler } from '@services/coralservices/AFTXCoralHandler';
import { SentryService } from '@services/coralservices/SentryService';
import { EnvironmentDetail } from '@utils/EnvironmentDetailUtils';
import { CoralExceptionFactory } from '@ch-types/CoralExceptionFactory';
import { RegionMappingService } from '@services/RegionMappingService';
import { Exception } from '@ch-types/Exception';

import _ from 'lodash';
import $ from 'jquery';
import { CountMetric } from '@src/metrics/CountMetric';
import { LatencyMetric } from '@src/metrics/LatencyMetric';

interface IRequestType {
    data: object;
    operation: string;
    [x: string]: any;
}

export class ServiceClient {
    private clientName;
    private client;
    private recordMetrics;

    constructor(receivedClientName: string, receivedClient, recordMetrics?, realm?: string) {
        this.clientName = receivedClientName;
        this.client = receivedClient;

        /**
         * TODO: STORE ISOLATION MIGRATION: CONDITIONAL to ensure backward compatibility
         * Once we migrate all the clients, we should always set the domain and realm.
         */
        if (this.client.hasOwnProperty('config')) {
            const domain = EnvironmentDetail.getDomain();
            const region = EnvironmentDetail.getRegion();
            realm = realm ?? EnvironmentDetail.getRealm();
            if (!_.isString(realm) || _.isEmpty(realm)) {
                try {
                    realm = RegionMappingService.getAwsRegionFromAirportCode(region);
                } catch (error) {
                    console.error('Invalid or missing region := ' + region + '; defaulting to us-west-2`');
                    realm = 'us-west-2';
                }
            }
            this.client.config.overrideRealm(realm);
            this.client.config.overrideDomain(domain);
            console.debug(`Store Specific Client := ${this.clientName} Domain := ${domain} Region := ${region} Realm := ${realm}`);
        }
        if (_.isUndefined(recordMetrics)) {
            this.recordMetrics = true;
        } else {
            this.recordMetrics = recordMetrics;
        }
    }

    public getClient() {
        /**
         * TODO: STORE ISOLATION MIGRATION
         * Remove if block once fully deprecated IhmJSCoralClient for all clients
         */
        if (!this.client.hasOwnProperty('post')) {
            console.debug(`${this.clientName} is not using a store-specific client builder`);
            return $[this.client];
        }
        return this.client;
    }

    /**
     * Sends the request to the service using the right client for AuthN (i.e. IhmLogin, IIB or Sentry).
     *
     * @param {object} request - The request to send
     * @param {string} storeId - The storeId for the request
     * @param {object} extraOptions - Optional object with extra options for the request
     *  Example:
     *      {
     *          sensitive: {boolean}, if set to true, we will not log anything in the request
     *          (note that this may make debugging harder)
     *      }
     */
    public async sendRequest(request: IRequestType, storeId: string, extraOptions?: object) {
        const requestData = _.cloneDeep(request);
        requestData.data = requestData.data || {}; // Ensure that the data is never unset
        let latencyMetric;
        let oldSendRequestMetric;
        let failureCountMetric;
        let faultCountMetric;
        if (storeId && this.recordMetrics) {
            oldSendRequestMetric = new CountMetric(storeId, 'oldFormatRequest', 'IhmFlyingDutchman', requestData.operation);
            oldSendRequestMetric.setClientProgram(this.clientName);
            latencyMetric = new LatencyMetric(storeId, 'latency', 'IhmFlyingDutchman', requestData.operation);
            latencyMetric.setClientProgram(this.clientName);
            failureCountMetric = new CountMetric(storeId, 'failures', 'IhmFlyingDutchman', requestData.operation);
            failureCountMetric.setClientProgram(this.clientName);
            faultCountMetric = new CountMetric(storeId, 'Fault', 'IhmFlyingDutchman', requestData.operation);
            faultCountMetric.setClientProgram(this.clientName);
            latencyMetric.start();
        }
        try {
            let resolvedResponse;
            if (EnvironmentDetail.isIIBAuthN()) {
                resolvedResponse = await IIBCoralHandler.sendRequest(requestData, this.getClient(), extraOptions);
            } else if (EnvironmentDetail.isAFTXAuthN()) {
                resolvedResponse = await AFTXCoralHandler.sendRequest(requestData, this.getClient(), extraOptions);
            } else {
                resolvedResponse = await SentryService.makeSentryCall(requestData, this.getClient(), extraOptions);
            }
            if (oldSendRequestMetric) {
                oldSendRequestMetric.count(0);
            }
            if (failureCountMetric) {
                failureCountMetric.count(0);
            }
            if (faultCountMetric) {
                faultCountMetric.count(0);
            }
            const responseMetadata = resolvedResponse.metadata || {};
            let isOldFormatResponse = true;
            let resolvedData;
            if (responseMetadata.hasMetadata) {
                isOldFormatResponse = responseMetadata.isOldFormatResponse || false;
                resolvedData = resolvedResponse.data;
            } else {
                resolvedData = resolvedResponse;
            }
            if (isOldFormatResponse && oldSendRequestMetric) {
                oldSendRequestMetric.count(1);
            }
            return Promise.resolve(resolvedData);
        } catch (rejectionResponse) {
            if (failureCountMetric) {
                failureCountMetric.count(1);
            }
            let rejectedData;
            let statusCode;
            const responseMetadata = rejectionResponse.metadata || {};
            let isOldFormatResponse = true;
            if (responseMetadata.hasMetadata) {
                isOldFormatResponse = responseMetadata.isOldFormatResponse || false;
                rejectedData = rejectionResponse.data || {};
                statusCode = responseMetadata.statusCode || 0;
            } else {
                rejectedData = rejectionResponse;
                statusCode = rejectionResponse.status || 0;
            }
            if (isOldFormatResponse && oldSendRequestMetric) {
                oldSendRequestMetric.count(1);
            }
            if (faultCountMetric && statusCode > 400) {
                faultCountMetric.count(1);
            }
            if (rejectedData instanceof Exception) {
                return Promise.reject(rejectedData);
            } else if (_.isString(rejectedData && rejectedData.__type)) {
                return Promise.reject(CoralExceptionFactory.buildCoralException(rejectedData.__type, rejectedData));
            } else {
                return Promise.reject(new Exception({ message: JSON.stringify(rejectedData) }));
            }
        } finally {
            if (latencyMetric) {
                latencyMetric.stop();
            }
        }
    }
}
